import {
  SessionStorageSlotPotRepository,
  SlotPot,
  type SlotPotLevelRuleMap,
  type SlotPotRecord,
  type SlotPotStoreAdapter,
} from '@slot-nx/pot'
import { probUtil } from '@slot-nx/prob'
import { gameConfig } from '../../config'
import { PotType } from '../game/model'
import { useGameStore } from '../game/store/GameStore'

// ---------------------------------------------------------------------------
// Pot rules (mirror of backend Freya.ThreeComboOlympus `Pot.LevelRuleMap`)
//
// Backend semantics (`Pot.Collect()`):
//   levelUpProb = LevelUpProbRate * CollectNum, capped at 1 — rolled BEFORE
//   the counter advances, and CollectNum only increments when the roll loses.
//   That ordering is why every pot here uses collectMode `scaling`.
// ---------------------------------------------------------------------------

export const POT_MIN_LEVEL = 1
export const POT_MAX_LEVEL = 5

const LEVEL_RULE_MAP: SlotPotLevelRuleMap = {
  1: { collectThreshold: 10, levelUpProb: 0.35 },
  2: { collectThreshold: 10, levelUpProb: 0.35 },
  3: { collectThreshold: 10, levelUpProb: 0.35 },
  4: { collectThreshold: 10, levelUpProb: 0.35 },
  5: { collectThreshold: 0, levelUpProb: 0 },
}

// ---------------------------------------------------------------------------
// Persistence layer (sessionStorage) — one blob per game, keyed per pot colour
// ---------------------------------------------------------------------------

const repo = new SessionStorageSlotPotRepository(gameConfig.name, sessionStorage)

/** Storage / store key for each of the three pots. */
export const POT_RECORD_KEY: Record<PotType, string> = {
  [PotType.Expanding1]: 'Expanding1',
  [PotType.Wild2]: 'Wild2',
  [PotType.Multiplier3]: 'Multiplier3',
}

function createInitialRecord(): SlotPotRecord {
  return { level: POT_MIN_LEVEL, collectNum: 0 }
}

/** Every write goes to Zustand AND sessionStorage atomically. */
function createStoreAdapter(key: string): SlotPotStoreAdapter {
  return {
    getRecord: () => useGameStore.getState().pots[key] ?? createInitialRecord(),
    setRecord: (record) => {
      useGameStore.setState(state => ({ pots: { ...state.pots, [key]: record } }))
      repo.setPotRecord(record, key)
        .catch(err => console.error(`[Pot] persist "${key}" failed`, err))
    },
    getPotStatusFromSessionStorage: async () => {
      const record = await repo.getPotRecord(key)
      // Fresh sessions come back as level 0 (repo default); this game is 1-based.
      const normalized = record.level < POT_MIN_LEVEL ? createInitialRecord() : record
      useGameStore.setState(state => ({ pots: { ...state.pots, [key]: normalized } }))
    },
  }
}

function createPot(type: PotType): SlotPot {
  const key = POT_RECORD_KEY[type]
  return new SlotPot(key, probUtil, LEVEL_RULE_MAP, createStoreAdapter(key), 'scaling')
}

// ---------------------------------------------------------------------------
// The three pot instances (R / G / B)
// ---------------------------------------------------------------------------

export const potMap: Record<PotType, SlotPot> = {
  [PotType.Expanding1]: createPot(PotType.Expanding1),
  [PotType.Wild2]: createPot(PotType.Wild2),
  [PotType.Multiplier3]: createPot(PotType.Multiplier3),
}

export const potList: SlotPot[] = Object.values(potMap)

/** Hydrate all three pots from sessionStorage; call once before the pots render. */
export async function loadPotsFromStorage(): Promise<void> {
  await Promise.all(potList.map(pot => pot.getPotStatusFromSessionStorage()))
}

export function getPotLevel(type: PotType): number {
  return potMap[type].storeAdapter.getRecord().level
}
